import { useEffect, useState, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button, Card, Spinner, ProgressBar } from "react-bootstrap";
import { getApi, postApi } from "../../../shared/services/apiHelper";
import { runCpm, projectDetail } from "../../../shared/settings/apiConfig";
import moment from "moment";
import "./ProgressCPM.scss";

const phaseColors = {
  "Substructure": { gradient: "from-purple-500 to-purple-600", bg: "#7c3aed", light: "#ede9fe" },
  "Superstructure": { gradient: "from-blue-500 to-blue-600", bg: "#2563eb", light: "#dbeafe" },
  "Foundation": { gradient: "from-orange-500 to-orange-600", bg: "#f97316", light: "#fff7ed" },
  "Plinth Beam": { gradient: "from-emerald-500 to-emerald-600", bg: "#059669", light: "#d1fae5" },
  "Column & Staircase": { gradient: "from-green-500 to-green-600", bg: "#22c55e", light: "#dcfce7" },
  "GF Beams & Slab": { gradient: "from-blue-500 to-blue-600", bg: "#3b82f6", light: "#dbeafe" },
  "Lintel Beams": { gradient: "from-purple-500 to-purple-600", bg: "#7c3aed", light: "#ede9fe" },
  "Wall & Finishes": { gradient: "from-emerald-500 to-emerald-600", bg: "#10b981", light: "#d1fae5" },
  "Masonry": { gradient: "from-orange-500 to-orange-600", bg: "#f97316", light: "#fff7ed" },
  "Services": { gradient: "from-purple-500 to-purple-600", bg: "#7c3aed", light: "#ede9fe" },
  "Finishes": { gradient: "from-emerald-500 to-emerald-600", bg: "#059669", light: "#d1fae5" },
  "Fittings": { gradient: "from-green-500 to-green-600", bg: "#16a34a", light: "#dcfce7" },
  "Handover": { gradient: "from-green-500 to-green-600", bg: "#059669", light: "#d1fae5" },
};

const getPhaseColor = (phase) => {
  return phaseColors[phase] || { gradient: "from-gray-500 to-gray-600", bg: "#6b7280", light: "#f3f4f6" };
};

const statusConfig = {
  completed: { color: "bg-green-100 text-green-700 border-green-200", label: "Completed", icon: "fa-check-circle" },
  in_progress: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "In Progress", icon: "fa-spinner" },
  not_started: { color: "bg-gray-100 text-gray-700 border-gray-200", label: "Not Started", icon: "fa-clock" },
  at_risk: { color: "bg-amber-100 text-amber-700 border-amber-200", label: "At Risk", icon: "fa-exclamation-triangle" },
};

const summaryCards = [
  { key: "totalTasks", label: "Total Tasks", subtitle: "All tasks", icon: "fa-tasks", gradient: "from-blue-500 to-blue-600", color: "#3b82f6", lightBg: "#dbeafe" },
  { key: "completedTasks", label: "Completed", subtitle: "Tasks completed", icon: "fa-check-circle", gradient: "from-emerald-500 to-emerald-600", color: "#10b981", lightBg: "#dcfce7" },
  { key: "inProgressTasks", label: "In Progress", subtitle: "Tasks in progress", icon: "fa-spinner", gradient: "from-amber-500 to-amber-600", color: "#f59e0b", lightBg: "#fef3c7" },
  { key: "notStartedTasks", label: "Not Started", subtitle: "Tasks not started", icon: "fa-clock", gradient: "from-gray-500 to-gray-600", color: "#6b7280", lightBg: "#f3f4f6" },
  { key: "criticalTasks", label: "Critical Path", subtitle: "Critical activities", icon: "fa-link", gradient: "from-red-500 to-red-600", color: "#ef4444", lightBg: "#fee2e2" },
  { key: "overallProgress", label: "Overall Progress", subtitle: "Project progress", icon: "fa-percent", gradient: "from-purple-500 to-purple-600", color: "#7c3aed", lightBg: "#ede9fe" },
];

// Task letter labels (A-Z repeated as needed)
const getTaskLetter = (idx) => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  return letters[idx % 26];
};

export default function ProgressCPM() {
  const { id: projectId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [cpmData, setCpmData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const projectResponse = await getApi(`${projectDetail}/${projectId}`, true);
      setProjectData(projectResponse.data);

      const response = await postApi(runCpm, {
        project_id: projectId,
        start_date: moment().format("YYYY-MM-DD")
      }, true);

      if (response.data) {
        setCpmData(response.data);
        // Expand first phase by default
        const phases = getUniquePhasesFromData(response.data);
        if (phases.length > 0) {
          setExpandedPhases({ [phases[0]]: true });
        }
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err?.response?.data?.detail || "Failed to load progress data");
      setLoading(false);
    }
  };

  const getUniquePhasesFromData = (data) => {
    if (!data?.stages) return [];
    const phases = [...new Set(data.stages.map((s) => s.phase))];
    return phases.filter(Boolean);
  };

  // Derive task statuses from CPM dates
  const getDerivedTasks = useMemo(() => {
    if (!cpmData?.all_activities) return [];
    const today = moment().startOf("day");

    return cpmData.all_activities.map((act) => {
      const es = moment(act.es);
      const ef = moment(act.ef);

      let status = "not_started";
      let progress = 0;

      if (today.isAfter(ef)) {
        status = "completed";
        progress = 100;
      } else if (today.isSameOrAfter(es) && today.isSameOrBefore(ef)) {
        status = "in_progress";
        // Estimate progress based on how far into the duration we are
        const totalDuration = ef.diff(es, "days") || 1;
        const elapsed = today.diff(es, "days");
        progress = Math.min(Math.round((elapsed / totalDuration) * 100), 99);

        // Check if it's at risk (little float and behind)
        if (act.total_float !== undefined && act.total_float <= 2) {
          status = "at_risk";
        }
      } else {
        status = "not_started";
        progress = 0;
      }

      return {
        ...act,
        derivedStatus: status,
        derivedProgress: progress,
        phase: act.phase || act.stage_label || "Other",
      };
    });
  }, [cpmData]);

  // Group tasks by phase
  const tasksByPhase = useMemo(() => {
    const grouped = {};
    getDerivedTasks.forEach((task) => {
      const phase = task.phase || "Other";
      if (!grouped[phase]) grouped[phase] = [];
      grouped[phase].push(task);
    });
    return grouped;
  }, [getDerivedTasks]);

  // Compute phase progress
  const phaseProgressMap = useMemo(() => {
    const map = {};
    Object.entries(tasksByPhase).forEach(([phase, tasks]) => {
      const total = tasks.length;
      const completed = tasks.filter((t) => t.derivedStatus === "completed").length;
      const avgProgress = Math.round(tasks.reduce((sum, t) => sum + t.derivedProgress, 0) / total);
      map[phase] = { total, completed, avgProgress };
    });
    return map;
  }, [tasksByPhase]);

  // Overall progress summary
  const progressSummary = useMemo(() => {
    const tasks = getDerivedTasks;
    if (tasks.length === 0) return null;
    const total = tasks.length;
    const completed = tasks.filter((t) => t.derivedStatus === "completed").length;
    const inProgress = tasks.filter((t) => t.derivedStatus === "in_progress" || t.derivedStatus === "at_risk").length;
    const notStarted = tasks.filter((t) => t.derivedStatus === "not_started").length;
    const critical = tasks.filter((t) => t.critical).length;
    const overallProgress = Math.round(tasks.reduce((sum, t) => sum + t.derivedProgress, 0) / total);
    const criticalCompleted = tasks.filter((t) => t.critical && t.derivedStatus === "completed").length;
    const criticalProgress = critical > 0 ? Math.round(tasks.filter((t) => t.critical).reduce((sum, t) => sum + t.derivedProgress, 0) / critical) : 0;

    return {
      totalTasks: total,
      completedTasks: completed,
      inProgressTasks: inProgress,
      notStartedTasks: notStarted,
      criticalTasks: critical,
      overallProgress,
      criticalProgress,
      criticalCompleted,
    };
  }, [getDerivedTasks]);

  const togglePhase = (phase) => {
    setExpandedPhases((prev) => ({ ...prev, [phase]: !prev[phase] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return moment(dateStr).format("DD MMM YY");
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5 main-content">
        <Spinner animation="border" variant="purple" />
        <span className="ms-3">Loading progress data...</span>
      </div>
    );
  }

  if (error && !cpmData) {
    return (
      <div className="text-center py-5 main-content">
        <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
        <h4 className="text-muted">{error}</h4>
        <Button className="secondary-btn-gradient mt-3" onClick={fetchData}>
          Retry
        </Button>
        <Button variant="link" onClick={() => history.goBack()}>
          Go Back
        </Button>
      </div>
    );
  }

  const phases = Object.keys(tasksByPhase);

  return (
    <div className="progress-cpm-container main-content animate-fade-in">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="d-flex align-items-center gap-3">
          <Button variant="link" className="p-0 text-dark" onClick={() => history.goBack()}>
            <i className="fa fa-arrow-left fa-lg"></i>
          </Button>
          <div className="d-flex align-items-center gap-2">
            <div className="total-icon rounded p-2" style={{ backgroundColor: "#059669" }}>
              <i className="fa fa-tasks text-white"></i>
            </div>
            <div>
              <h3 className="content-label mb-0">Task Progress Tracking</h3>
              <div className="d-flex align-items-center gap-2 text-muted text-sm mt-1">
                <span>{projectData?.name || "Project"}</span>
                <span className="mx-1">•</span>
                <span>Date-based progress inference</span>
              </div>
            </div>
          </div>
        </div>
        <Button
          className="btn-3d-outline fw-bold"
          onClick={() => history.push(`/projects/${projectId}/cpm`)}
        >
          <i className="fa fa-sitemap mr-2"></i> View CPM Plan
        </Button>
      </div>

      {/* Summary Cards - Reference Style */}
      {progressSummary && (
        <div className="row g-3 mb-4">
          {summaryCards.map((card) => {
            const value = progressSummary[card.key];
            const isPercent = card.key === "overallProgress";
            return (
              <div key={card.key} className="col-md-2 col-6">
                <div className="card-3d p-3 h-100">
                  <div className="d-flex align-items-center gap-3 h-100">
                    <div
                      className={`summary-card-icon d-inline-flex align-items-center justify-content-center rounded bg-gradient-to-br ${card.gradient}`}
                      style={{ width: "40px", height: "40px", flexShrink: 0, borderRadius: "10px" }}
                    >
                      <i className={`fa ${card.icon} text-white`} style={{ fontSize: "1rem" }}></i>
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-muted mb-0" style={{ fontSize: "0.975rem", lineHeight: 1.2, whiteSpace: "nowrap", fontWeight: "800" }}>{card.label}</p>
                      <h4
                        className={`font-weight-bold mb-0 ${isPercent ? "gradient-text" : ""}`}
                        style={{ fontSize: "1.5rem", color: isPercent ? undefined : "#111827" }}
                      >
                        {value}{isPercent ? "%" : ""}
                      </h4>
                      <p className="text-muted mb-0" style={{ fontSize: "0.780rem", lineHeight: 1.2, marginTop: "2px", fontWeight: "500" }}>{card.subtitle}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Overall Progress Bar */}
      {progressSummary && (
        <Card className="activity-card p-4 mb-4">
          <div className="d-flex align-items-center gap-3 mb-3">
            <div
              className="p-2 rounded bg-gradient-to-br from-purple-500 to-purple-600 d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px" }}
            >
              <i className="fa fa-chart-line text-white"></i>
            </div>
            <div className="flex-grow-1">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <h5 className="font-weight-bold mb-0">Overall Project Progress</h5>
                <span className="font-weight-bold gradient-text" style={{ fontSize: "1.25rem" }}>
                  {progressSummary.overallProgress}%
                </span>
              </div>
              <ProgressBar
                now={progressSummary.overallProgress}
                variant="purple"
                className="progress-cpm-bar"
                style={{ height: "10px", borderRadius: "5px" }}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Progress by Phase - Ring Style */}
      {phases.length > 0 && (
        <Card className="activity-card p-4 mb-4">
          <div className="d-flex align-items-center gap-2 mb-4">
            <div
              className="p-2 rounded bg-gradient-to-br from-purple-500 to-purple-600 d-flex align-items-center justify-content-center"
              style={{ width: "36px", height: "36px" }}
            >
              <i className="fa fa-layer-group text-white"></i>
            </div>
            <h5 className="font-weight-bold mb-0">Progress by Phase</h5>
          </div>
          <div className="row g-3">
            {phases.map((phase) => {
              const info = phaseProgressMap[phase];
              const colors = getPhaseColor(phase);
              const progress = info.avgProgress || 0;
              const radius = 32;
              const circumference = 2 * Math.PI * radius;
              const dashOffset = circumference * (1 - progress / 100);
              return (
                <div key={phase} className="col-md-4 col-lg-2 col-6">
                  <div className="phase-ring-card">
                    <div className="phase-ring-label" style={{ color: colors.bg }}>
                      {phase}
                    </div>
                    <div className="phase-ring-visual">
                      <svg width="100" height="100" viewBox="0 0 100 100" className="phase-ring-svg">
                        {/* Background track ring */}
                        <circle
                          cx="50"
                          cy="50"
                          r={radius}
                          fill="none"
                          stroke={colors.light}
                          strokeWidth="6"
                        />
                        {/* Progress arc ring */}
                        <circle
                          cx="50"
                          cy="50"
                          r={radius}
                          fill="none"
                          stroke={colors.bg}
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeDasharray={circumference}
                          strokeDashoffset={dashOffset}
                          transform="rotate(-90 50 50)"
                          className="phase-ring-arc"
                        />
                      </svg>
                      <div className="phase-ring-center-text" style={{ color: colors.bg }}>
                        <span className="phase-ring-percent">{progress}%</span>
                      </div>
                    </div>
                    <div className="phase-ring-footer">
                      <span className="phase-ring-task-count">{info.completed}/{info.total} tasks</span>
                      <div
                        className="phase-ring-arrow"
                        style={{ backgroundColor: colors.bg }}
                      >
                        <i className="fa fa-arrow-right text-white" style={{ fontSize: "0.865rem" }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Tasks by Phase - Updated Design */}
      <div className="space-y-3">
        {phases.length === 0 && (
          <Card className="activity-card p-4 text-center">
            <i className="fa fa-clipboard-list fa-3x text-muted mb-3"></i>
            <h5>No Tasks Available</h5>
            <p className="text-muted">Run CPM analysis to generate tasks for progress tracking.</p>
            <Button className="secondary-btn-gradient" onClick={() => history.push(`/projects/${projectId}/cpm`)}>
              <i className="fa fa-sitemap mr-2"></i> Go to CPM
            </Button>
          </Card>
        )}

        {phases.map((phase) => {
          const tasks = tasksByPhase[phase];
          const info = phaseProgressMap[phase];
          const colors = getPhaseColor(phase);
          const isExpanded = expandedPhases[phase];

          return (
            <Card key={phase} className={`activity-card overflow-hidden phase-task-card ${!isExpanded ? 'phase-collapsed' : 'phase-expanded'}`} style={!isExpanded ? { borderColor: colors.bg, borderWidth: '1.5px' } : {}}>
              {/* Phase Header - with colored styling */}
              <button
                className={`phase-header w-100 p-3 d-flex align-items-center justify-content-between cursor-pointer ${isExpanded ? 'phase-header-expanded' : ''}`}
                onClick={() => togglePhase(phase)}
                style={{ border: "none", background: isExpanded ? colors.light : "#fff" }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="phase-header-icon d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: "42px",
                      height: "42px",
                      backgroundColor: isExpanded ? "#fff" : colors.light,
                      color: colors.bg,
                      flexShrink: 0,
                    }}
                  >
                    <i className="fa fa-layer-group" style={{ fontSize: "1.1rem" }}></i>
                  </div>
                  <div className="text-start">
                    <h6 className="font-weight-bold mb-0 phase-header-title" style={{ color: "#1f2937" }}>{phase}</h6>
                    <small className="phase-header-subtitle">{info.completed}/{info.total} tasks completed</small>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span className="font-weight-bold phase-header-percent" style={{ color: colors.bg, fontSize: "1.25rem", lineHeight: 1 }}>
                    {info.avgProgress}%
                  </span>
                  <i className={`fa ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"} phase-chevron`} style={{ color: colors.bg }}></i>
                </div>
              </button>

              {/* Task rows - Updated Design */}
              {isExpanded && (
                <div className="phase-tasks-list">
                  {tasks.map((task, idx) => {
                    const StatusIcon = statusConfig[task.derivedStatus]?.icon || "fa-clock";
                    const statusLabel = statusConfig[task.derivedStatus]?.label || "Unknown";
                    const letterId = getTaskLetter(idx);

                    return (
                      <div
                        key={idx}
                        className={`task-row ${
                          task.critical ? "task-critical" : ""
                        }`}
                      >
                        {/* Letter badge column */}
                        <div className="task-col-letter">
                          <div
                            className="task-letter-badge"
                            style={{
                              backgroundColor: task.derivedStatus === "completed" ? "#d1fae5" :
                                task.derivedStatus === "in_progress" ? "#dbeafe" :
                                task.derivedStatus === "at_risk" ? "#fef3c7" : "#f3f4f6",
                              color: task.derivedStatus === "completed" ? "#065f46" :
                                task.derivedStatus === "in_progress" ? "#1e40af" :
                                task.derivedStatus === "at_risk" ? "#92400e" : "#6b7280",
                            }}
                          >
                            {letterId}
                          </div>
                        </div>

                        {/* Task name + CRITICAL chip column */}
                        <div className="task-col-name">
                          <span className="task-name">{task.label}</span>
                          {task.critical && (
                            <span className="critical-chip">CRITICAL</span>
                          )}
                        </div>

                        {/* ES date column */}
                        <div className="task-col-es">
                          <span className="task-meta-item">
                            <i className="fa fa-calendar"></i>
                            <span className="meta-label">ES:</span> {formatDate(task.es)}
                          </span>
                        </div>

                        {/* EF date column */}
                        <div className="task-col-ef">
                          <span className="task-meta-item">
                            <span className="meta-label">EF:</span> {formatDate(task.ef)}
                          </span>
                        </div>

                        {/* Duration column */}
                        <div className="task-col-duration">
                          <span className="task-meta-item">
                            <i className="fa fa-clock"></i>
                            {task.duration} days
                          </span>
                        </div>

                        {/* Progress bar column */}
                        <div className="task-col-progress">
                          <div className="task-progress-track">
                            <div
                              className={`task-progress-fill ${task.derivedStatus === "completed" ? "fill-completed" : task.derivedStatus === "in_progress" ? "fill-in-progress" : task.derivedStatus === "at_risk" ? "fill-at-risk" : "fill-not-started"}`}
                              style={{ width: `${task.derivedProgress}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Percentage column */}
                        <div className="task-col-percent">
                          <span className="task-progress-text">{task.derivedProgress}%</span>
                        </div>

                        {/* Status badge column */}
                        <div className="task-col-status">
                          <span className={`task-status-badge ${task.derivedStatus === "completed" ? "badge-completed" : task.derivedStatus === "in_progress" ? "badge-in-progress" : task.derivedStatus === "at_risk" ? "badge-at-risk" : "badge-not-started"}`}>
                            <i className={`fa ${StatusIcon}`}></i>
                            {statusLabel}
                          </span>
                        </div>

                        {/* Assessment button column */}
                        <div className="task-col-action">
                          <Button className="assessment-btn">
                            Assessment <i className="fa fa-chevron-down"></i>
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// Helper to calculate position and width for the mini gantt bar
function getPositionPercent(dateStr, startStr, endStr) {
  const start = moment(startStr);
  const end = moment(endStr);
  const date = moment(dateStr);
  const totalDays = end.diff(start, "days") || 1;
  const offset = date.diff(start, "days");
  return Math.max(0, (offset / totalDays) * 100);
}

function getWidthPercent(startStr, endStr, projectStart, projectEnd) {
  const start = moment(projectStart);
  const end = moment(projectEnd);
  const taskStart = moment(startStr);
  const taskEnd = moment(endStr);
  const totalDays = end.diff(start, "days") || 1;
  const taskDays = taskEnd.diff(taskStart, "days") || 1;
  return Math.max(1, (taskDays / totalDays) * 100);
}